import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { ProtectedRoute } from '@/components/protected-route';
import { MainLayout } from '@/components/main-layout';
import {
  Card,
  CardContent,
  CardHeader,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useJournals } from '@/hooks/use-journals';
import { useGeneralLedger, useFiscalPeriods, useClosePeriod } from '@/hooks/use-accounting';
import { useOrders } from '@/hooks/use-orders';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { PageHeader } from '@/components/page-header';
import { Loader2, BookOpen, List, BarChart3, ReceiptText, FolderTree, CalendarDays, Eye } from 'lucide-react';
import { ChartOfAccountsTab } from '@/components/accounting/account-tree';
import { format } from 'date-fns';
import { Link } from '@tanstack/react-router';
import { clampPage, resolveTotalPages } from '@/lib/pagination';

export const Route = createFileRoute('/accounting')({
  component: () => (
    <ProtectedRoute>
      <MainLayout>
        <AccountingExplorerPage />
      </MainLayout>
    </ProtectedRoute>
  ),
});

function formatDate(dateStr: string) {
  try {
    return format(new Date(dateStr), 'dd MMM yyyy');
  } catch {
    return dateStr;
  }
}

function formatCurrency(amount: number | string) {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(num || 0);
}

function AccountingExplorerPage() {
  const [activeTab, setActiveTab] = useState('journal-entries');

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader title="Accounting Data Explorer" />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid h-auto w-full grid-cols-2 lg:grid-cols-6 lg:w-auto">
          <TabsTrigger value="fiscal-periods" className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            Fiscal Periods
          </TabsTrigger>
          <TabsTrigger value="journal-entries" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Journal Entries
          </TabsTrigger>
          <TabsTrigger value="journal-lines" className="flex items-center gap-2">
            <List className="h-4 w-4" />
            Journal Lines
          </TabsTrigger>
          <TabsTrigger value="chart-of-accounts" className="flex items-center gap-2">
            <FolderTree className="h-4 w-4" />
            Chart of Accounts
          </TabsTrigger>
          <TabsTrigger value="general-ledger" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            General Ledger
          </TabsTrigger>
          <TabsTrigger value="invoices" className="flex items-center gap-2">
            <ReceiptText className="h-4 w-4" />
            Invoices
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="fiscal-periods">
            <FiscalPeriodsTab />
          </TabsContent>
          <TabsContent value="journal-entries">
            <JournalEntriesTab />
          </TabsContent>
          <TabsContent value="journal-lines">
            <JournalLinesTab />
          </TabsContent>
          <TabsContent value="chart-of-accounts">
            <ChartOfAccountsTab />
          </TabsContent>
          <TabsContent value="general-ledger">
            <GeneralLedgerTab />
          </TabsContent>
          <TabsContent value="invoices">
            <InvoicesTab />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

function FiscalPeriodsTab() {
  const { data: res, isLoading } = useFiscalPeriods();
  const periods = res?.data || [];
  const { mutate: closePeriod, isPending } = useClosePeriod();
  const { toast } = useToast();

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period Name</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {periods.map((period: any) => (
                  <TableRow key={period.id}>
                    <TableCell className="font-medium">{period.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(period.startDate)} - {formatDate(period.endDate)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={period.isClosed ? "secondary" : "default"}>
                        {period.isClosed ? "Closed" : "Open"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={period.isClosed || isPending}
                        onClick={() => {
                           if(confirm(`Are you sure you want to close the period ${period.name}? This will generate closing entries and cannot be easily undone.`)) {
                             closePeriod(period.id, {
                               onSuccess: () => {
                                 toast({
                                   title: "Period Closed",
                                   description: `Successfully closed period ${period.name} and generated entries.`,
                                 });
                               },
                               onError: (err: any) => {
                                 toast({
                                   variant: "destructive",
                                   title: "Failed to close period",
                                   description: err.message || "An error occurred",
                                 });
                               }
                             });
                           }
                        }}
                      >
                        {period.isClosed ? "Closed" : (isPending ? "Closing..." : "Close Period")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {periods.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-10 text-muted-foreground">
                      No fiscal periods found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function JournalEntriesTab() {
  const [page, setPage] = useState(1);
  const { data: res, isLoading } = useJournals({ page });
  const journals = res?.data || [];
  const totalPages = resolveTotalPages(res?.pagination, journals.length);

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col gap-4">
      {journals.map((journal: any) => (
        <Card key={journal.id} className="overflow-hidden">
          <CardHeader className="bg-muted/50 py-3">
            <div className="flex justify-between items-center text-sm font-medium">
              <div className="flex gap-4">
                <span className="text-primary font-bold">#{journal.journalNumber || journal.refNo}</span>
                <span>{formatDate(journal.journalDate || journal.date)}</span>
              </div>
              <Badge variant="outline">{journal.source}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{journal.description}</p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/2">Account</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {journal.lines?.map((line: any) => {
                    const isDebit = parseFloat(line.debit) > 0;
                    return (
                      <TableRow key={line.id}>
                        <TableCell className={isDebit ? "pl-4" : "pl-10 text-muted-foreground"}>
                          <div className="flex flex-col">
                            <span className="font-medium">{line.account?.name}</span>
                            <span className="text-[10px] uppercase font-mono">{line.account?.code}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {isDebit ? formatCurrency(line.debit) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">
                          {!isDebit ? formatCurrency(line.credit) : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ))}
      {res?.pagination && (
        <PaginationControls
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(nextPage) => setPage(clampPage(nextPage, totalPages))}
        />
      )}
    </div>
  );
}

function JournalLinesTab() {
  const [page, setPage] = useState(1);
  const { data: res, isLoading } = useJournals({ page });
  const totalPages = resolveTotalPages(res?.pagination, res?.data?.length ?? 0);
  
  // Flatten journals to lines
  const allLines = res?.data?.flatMap((j: any) => 
    j.lines?.map((l: any) => ({
      ...l,
      journalNo: j.journalNumber || j.refNo,
      date: j.journalDate || j.date,
      entryDescription: j.description
    })) || []
  ) || [];

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="hidden md:table-cell">Date</TableHead>
                  <TableHead className="hidden md:table-cell">Journal #</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allLines.map((line: any) => (
                  <TableRow key={line.id}>
                    <TableCell className="text-xs hidden md:table-cell">{formatDate(line.date)}</TableCell>
                    <TableCell className="font-medium text-xs hidden md:table-cell">#{line.journalNo}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{line.account?.name}</span>
                        <span className="text-[10px] text-muted-foreground">{line.account?.code}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {parseFloat(line.debit) > 0 ? formatCurrency(line.debit) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {parseFloat(line.credit) > 0 ? formatCurrency(line.credit) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      {res?.pagination && (
        <PaginationControls
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(nextPage) => setPage(clampPage(nextPage, totalPages))}
        />
      )}
    </div>
  );
}

function GeneralLedgerTab() {
  const [page, setPage] = useState(1);
  const { data: res, isLoading } = useGeneralLedger({ page });
  const ledgers = res?.data || [];
  const totalPages = resolveTotalPages(res?.pagination, ledgers.length);

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead className="hidden md:table-cell">Period</TableHead>
                  <TableHead className="text-right hidden md:table-cell">Opening</TableHead>
                  <TableHead className="text-right">Closing</TableHead>
                  <TableHead className="text-right">Net Change</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledgers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                      No ledger data found. Data appears after fiscal period closes.
                    </TableCell>
                  </TableRow>
                ) : ledgers.map((gl: any) => {
                  const net = parseFloat(gl.closingBal) - parseFloat(gl.openingBal);
                  return (
                    <TableRow key={gl.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{gl.account?.name}</span>
                          <span className="text-xs text-muted-foreground">{gl.account?.code}</span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="flex flex-col">
                          <span className="text-sm">{gl.period?.name}</span>
                          <span className="text-[10px] text-muted-foreground">
                            {formatDate(gl.period?.startDate)} - {formatDate(gl.period?.endDate)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono hidden md:table-cell">{formatCurrency(gl.openingBal)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(gl.closingBal)}</TableCell>
                      <TableCell className={`text-right font-mono font-bold ${net < 0 ? 'text-destructive' : 'text-green-600'}`}>
                        {formatCurrency(net)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      {res?.pagination && (
        <PaginationControls
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(nextPage) => setPage(clampPage(nextPage, totalPages))}
        />
      )}
    </div>
  );
}

function InvoicesTab() {
  const [page, setPage] = useState(1);
  const { data: res, isLoading } = useOrders({ page });
  const invoices = res?.data || [];
  const totalPages = resolveTotalPages(res?.pagination, invoices.length);

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead className="hidden md:table-cell">Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="hidden md:table-cell">Status</TableHead>
                  <TableHead className="text-right hidden md:table-cell">Subtotal</TableHead>
                  <TableHead className="text-right hidden md:table-cell">Tax</TableHead>
                  <TableHead className="text-right font-bold">Total</TableHead>
                  <TableHead className="text-right w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv: any) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-bold text-sm">
                      {inv.orderNumber || inv.invoiceNo}
                    </TableCell>
                    <TableCell className="text-xs hidden md:table-cell">
                      {formatDate(inv.orderDate || inv.createdAt)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {inv.customer?.name || '-'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge variant={inv.paymentStatus === 'PAID' ? 'default' : 'destructive'} className="text-[10px]">
                        {inv.paymentStatus}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-xs hidden md:table-cell">{formatCurrency(inv.subtotal || 0)}</TableCell>
                    <TableCell className="text-right text-xs hidden md:table-cell">{formatCurrency(inv.taxAmount || 0)}</TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(inv.totalAmount)}</TableCell>
                    <TableCell className="text-right">
                      <Link to="/orders/$id" params={{ id: inv.id }}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-lg hover:bg-primary/10 hover:text-primary transition-colors"
                          title="View invoice details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
                {invoices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                      No invoices found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      {res?.pagination && (
        <PaginationControls
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(nextPage) => setPage(clampPage(nextPage, totalPages))}
        />
      )}

    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex justify-center items-center py-20">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}
